/**
 * GatewayDashboardPageV2 — proper v2 redesign (G120 → G144).
 *
 * 4-tab dashboard mirroring v1 GatewayDashboardPage:
 *   1. Overview  — KPIs + key distribution + key types + top active keys
 *   2. Keys      — compact mirror with link out to full /gateway-keys inventory
 *   3. Security  — security stat tiles + filterable security event log
 *   4. Audit     — filterable audit trail with 10/50/100 pagination
 *
 * All v1 backend code paths preserved:
 *   - api.gatewayHealth, api.gatewayListKeys, api.gatewayGetTenantAudit
 *
 * v1 stays as fallback via /gateway-dashboard?legacy=1.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { isRebirthAdministerEnabled } from '../utils/featureFlags';
import { usePersistedState } from '../hooks/usePersistedState';
import * as api from '../services/api';
import {
  RefreshCw, Key, Shield, AlertTriangle, CheckCircle, Activity,
  ArrowRight, Server, Zap, Globe, FileText, Lock,
} from 'lucide-react';
import {
  Button, Pill, Card, MetricCard, Table, TabNav, EmptyState, FacetRail,
} from '../components/v2';
import { Pagination, usePaginationState } from '../components/common/Pagination';

import GatewayDashboardPage from './GatewayDashboardPage';

/* Tab definitions mirror v1 — Overview / Keys / Security / Audit. */
const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'keys',     label: 'Keys' },
  { id: 'security', label: 'Security' },
  { id: 'audit',    label: 'Audit' },
];

const SECURITY_EVENT_TYPES = new Set([
  'AUTH_FAILURE', 'IP_BLOCKED', 'RATE_LIMIT_HIT',
  'REVOKED', 'SUSPENDED', 'QUOTA_EXCEEDED',
]);

const EVENT_TONE = {
  AUTH_FAILURE:    'danger',
  IP_BLOCKED:      'danger',
  RATE_LIMIT_HIT:  'warning',
  REVOKED:         'muted',
  SUSPENDED:       'warning',
  QUOTA_EXCEEDED:  'warning',
  CREATED:         'success',
  ROTATED:         'cyan',
};

function fmtRelative(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  const diff = Date.now() - d.getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

function GatewayDashboardPageV2Inner() {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [tab, setTab] = usePersistedState('gateway-dash.tab', 'overview');
  const [health, setHealth] = useState(null);
  const [keys, setKeys]     = useState([]);
  const [audit, setAudit]   = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [h, k, a] = await Promise.all([
        api.gatewayHealth?.()?.catch(() => null),
        api.gatewayListKeys?.()?.catch(() => null),
        api.gatewayGetTenantAudit?.()?.catch(() => null),
      ]);
      setHealth(h?.data || h || null);
      const keyList = Array.isArray(k?.data?.items) ? k.data.items
                   : Array.isArray(k?.items) ? k.items
                   : Array.isArray(k?.data) ? k.data
                   : Array.isArray(k) ? k : [];
      setKeys(keyList);
      const auditList = Array.isArray(a?.data?.items) ? a.data.items
                     : Array.isArray(a?.items) ? a.items
                     : Array.isArray(a?.data) ? a.data
                     : Array.isArray(a) ? a : [];
      setAudit(auditList);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  /* Aggregate stats once — used across Overview, Security, and Audit tabs. */
  const keyStats = useMemo(() => {
    const total = keys.length;
    const active    = keys.filter(k => (k.status || 'ACTIVE') === 'ACTIVE').length;
    const rotating  = keys.filter(k => k.status === 'ROTATING').length;
    const suspended = keys.filter(k => k.status === 'SUSPENDED').length;
    const revoked   = keys.filter(k => k.status === 'REVOKED' || k.status === 'EXPIRED').length;
    const gatewayKeys = keys.filter(k => (k.keyType || 'GATEWAY') === 'GATEWAY' || k.scope?.includes('full')).length;
    const sdkKeys     = total - gatewayKeys;
    const totalRequests = keys.reduce((s, k) => s + (k.requests24h || k.totalRequests || 0), 0);
    const totalErrors   = keys.reduce((s, k) => s + (k.errors24h   || k.totalErrors   || 0), 0);
    const totalBlocked  = keys.reduce((s, k) => s + (k.rateLimitHits24h || k.blocked || 0), 0);
    const successRate = totalRequests > 0
      ? Math.max(0, Math.round(((totalRequests - totalErrors) / totalRequests) * 100))
      : 100;
    return { total, active, rotating, suspended, revoked, gatewayKeys, sdkKeys, totalRequests, totalErrors, totalBlocked, successRate };
  }, [keys]);

  const auditStats = useMemo(() => {
    const counts = {};
    for (const e of audit) {
      const t = e.eventType || e.action || 'OTHER';
      counts[t] = (counts[t] || 0) + 1;
    }
    return counts;
  }, [audit]);

  const securityEvents = useMemo(
    () => audit.filter(e => SECURITY_EVENT_TYPES.has(e.eventType || e.action)),
    [audit],
  );

  const isUp = (health?.status || 'UP') === 'UP';

  return (
    <div className="min-h-full bg-v2-page text-v2-ink p-4 sm:p-7 max-w-[1500px] mx-auto">
      <header className="flex items-start gap-4 mb-5">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight m-0">Protected Gateway</h1>
          <p className="text-[13px] text-v2-ink-secondary mt-0.5">
            Live traffic, auth posture, key inventory, and security telemetry at the protected gateway.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={reload}>Refresh</Button>
          <Button variant="primary" size="sm" iconLeft={<Key size={13} />}
            onClick={() => navigate('/gateway-keys')}>Manage API keys</Button>
        </div>
      </header>

      {/* Health banner — always visible across tabs. */}
      <div className={'rounded-v2-md border-l-4 px-4 py-3 mb-5 flex items-center gap-3 ' +
        (isUp ? 'border-v2-success bg-v2-success-soft' : 'border-v2-danger bg-v2-danger-soft')}>
        <div className={'w-9 h-9 rounded-full flex items-center justify-center ' + (isUp ? 'bg-v2-success-soft' : 'bg-v2-danger-soft')}>
          {isUp
            ? <CheckCircle size={20} className="text-v2-success" />
            : <AlertTriangle size={20} className="text-v2-danger" />}
        </div>
        <div className="flex-1">
          <div className="text-[14px] font-bold text-v2-ink">
            Gateway: {isUp ? 'Operational' : 'Degraded'}
          </div>
          <div className="text-[12px] text-v2-ink-secondary">
            {health?.message || (isUp
              ? `All systems nominal · last check ${new Date().toLocaleTimeString()}`
              : 'Issue detected — see Security tab for events.')}
          </div>
        </div>
      </div>

      <TabNav tabs={TABS} activeId={tab} onChange={setTab} ariaLabel="Gateway sections" />
      <div className="my-4" />

      {tab === 'overview'  && <OverviewTab navigate={navigate} setTab={setTab} keys={keys} keyStats={keyStats} securityEvents={securityEvents} audit={audit} />}
      {tab === 'keys'      && <KeysTab navigate={navigate} keys={keys} keyStats={keyStats} />}
      {tab === 'security'  && <SecurityTab securityEvents={securityEvents} auditStats={auditStats} loading={loading} />}
      {tab === 'audit'     && <AuditTab audit={audit} loading={loading} />}
    </div>
  );
}

/* ── Overview tab ──────────────────────────────────────────────── */
function OverviewTab({ navigate, setTab, keys, keyStats, securityEvents, audit }) {
  const topActiveKeys = useMemo(
    () => [...keys].sort((a, b) => (b.requests24h || b.totalRequests || 0) - (a.requests24h || a.totalRequests || 0)).slice(0, 5),
    [keys],
  );
  const recentEvents = useMemo(() => audit.slice(0, 6), [audit]);

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <MetricCard label="Total requests / 24h" value={keyStats.totalRequests.toLocaleString()} tone="cyan" />
        <MetricCard label="Success rate" value={`${keyStats.successRate}%`}
          tone={keyStats.successRate >= 99 ? 'success' : keyStats.successRate >= 95 ? 'warning' : 'danger'} />
        <MetricCard label="Blocked / rate-limited" value={keyStats.totalBlocked.toLocaleString()}
          tone={keyStats.totalBlocked > 0 ? 'warning' : 'success'} />
        <MetricCard label="Active keys" value={`${keyStats.active} / ${keyStats.total}`} tone="action" />
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mb-5">
        <Card>
          <Card.Header title="Key distribution" />
          <Card.Body>
            <div className="space-y-3">
              {[
                { label: 'Active',            value: keyStats.active,    tone: 'success' },
                { label: 'Rotating',          value: keyStats.rotating,  tone: 'warning' },
                { label: 'Suspended',         value: keyStats.suspended, tone: 'danger' },
                { label: 'Revoked / Expired', value: keyStats.revoked,   tone: 'muted' },
              ].map(({ label, value, tone }) => {
                const pct = keyStats.total > 0 ? Math.round((value / keyStats.total) * 100) : 0;
                const barColor =
                  tone === 'success' ? 'bg-v2-success'
                  : tone === 'warning' ? 'bg-v2-warning'
                  : tone === 'danger' ? 'bg-v2-danger'
                  : 'bg-v2-ink-muted';
                return (
                  <div key={label} className="flex items-center gap-3">
                    <Pill tone={tone} size="sm" dot>{label}</Pill>
                    <div className="flex-1 h-1.5 bg-v2-muted rounded-full overflow-hidden">
                      <div className={`h-full ${barColor}`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="font-mono-v2 text-[12px] text-v2-ink w-10 text-right">{value}</span>
                  </div>
                );
              })}
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header title="Key types" />
          <Card.Body>
            <div className="space-y-3">
              <div className="flex items-center gap-3 bg-v2-muted rounded-v2-sm p-3 border border-v2-border-subtle">
                <div className="w-10 h-10 rounded-v2-sm bg-v2-action-soft flex items-center justify-center">
                  <Server size={20} className="text-v2-action" />
                </div>
                <div className="flex-1">
                  <div className="text-[13px] font-semibold text-v2-ink">Gateway keys</div>
                  <div className="text-[11.5px] text-v2-ink-muted">Full proxy invocation</div>
                </div>
                <span className="text-[24px] font-extrabold tabular-nums text-v2-ink">{keyStats.gatewayKeys}</span>
              </div>
              <div className="flex items-center gap-3 bg-v2-muted rounded-v2-sm p-3 border border-v2-border-subtle">
                <div className="w-10 h-10 rounded-v2-sm bg-v2-trust-soft flex items-center justify-center">
                  <Zap size={20} className="text-v2-trust" />
                </div>
                <div className="flex-1">
                  <div className="text-[13px] font-semibold text-v2-ink">SDK keys</div>
                  <div className="text-[11.5px] text-v2-ink-muted">Split pre / post protection</div>
                </div>
                <span className="text-[24px] font-extrabold tabular-nums text-v2-ink">{keyStats.sdkKeys}</span>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2">
          <Card.Header title={`Top active keys · ${topActiveKeys.length}`} actions={
            <Button variant="ghost" size="sm" iconLeft={<ArrowRight size={13} />}
              onClick={() => navigate('/gateway-keys')}>Full inventory</Button>
          } />
          <Card.Body>
            {topActiveKeys.length === 0 ? (
              <EmptyState icon={<Key size={20} />} title="No key activity yet"
                description="Active keys with requests in the last 24h will appear here." />
            ) : (
              <Table
                ariaLabel="Top active keys"
                density="compact"
                columns={[
                  { key: 'name', header: 'Key', render: (r) => (
                    <div>
                      <div className="font-semibold text-v2-ink text-[12.5px]">{r.name || r.label || r.id?.slice(0, 8)}</div>
                      <div className="text-[10.5px] text-v2-ink-muted font-mono-v2">{(r.id || '').slice(0, 12)}…</div>
                    </div>
                  )},
                  { key: 'scope', header: 'Scope', render: (r) => (
                    <Pill tone="muted" size="sm">{r.scope || 'tenant'}</Pill>
                  )},
                  { key: 'reqs', header: 'Reqs / 24h', align: 'right', render: (r) => (
                    <span className="font-mono-v2 text-[12px] text-v2-ink">{(r.requests24h || r.totalRequests || 0).toLocaleString()}</span>
                  )},
                  { key: 'status', header: 'Status', render: (r) => (
                    <Pill tone={(r.status || 'ACTIVE') === 'ACTIVE' ? 'success' : 'muted'} size="sm" dot>
                      {(r.status || 'ACTIVE').toLowerCase()}
                    </Pill>
                  )},
                ]}
                rows={topActiveKeys}
                rowKey={(r) => r.id}
                onRowClick={() => navigate('/gateway-keys')}
              />
            )}
          </Card.Body>
        </Card>

        <Card>
          <Card.Header title="Security pulse" actions={
            /* G203 — Wire dead "—" placeholder. Switches to the Security
             * tab which already has the full filterable security event log. */
            <Button variant="ghost" size="sm" iconLeft={<ArrowRight size={13} />}
              onClick={() => setTab && setTab('security')}>
              View all
            </Button>
          } />
          <Card.Body>
            {securityEvents.length === 0 ? (
              <p className="text-[12.5px] text-v2-success-strong">
                <CheckCircle size={14} className="inline mr-1.5" />No security events in this window.
              </p>
            ) : (
              <ul className="space-y-2 text-[12px]">
                {securityEvents.slice(0, 5).map((e, i) => (
                  <li key={e.id || i} className="border-l-2 border-v2-warning pl-2">
                    <div className="font-semibold text-v2-ink">{e.eventType || e.action}</div>
                    <div className="text-[11px] text-v2-ink-muted">{e.reason || e.details || e.message || '—'}</div>
                    <div className="text-[10.5px] text-v2-ink-muted font-mono-v2">{fmtRelative(e.createdAt || e.timestamp)}</div>
                  </li>
                ))}
              </ul>
            )}
          </Card.Body>
        </Card>
      </div>

      <Card className="mt-5">
        <Card.Header title="Recent audit trail" />
        <Card.Body>
          {recentEvents.length === 0 ? (
            <p className="text-v2-ink-muted text-[12.5px]">No audit events recorded.</p>
          ) : (
            <Table
              ariaLabel="Recent audit"
              density="compact"
              columns={[
                { key: 'time', header: 'When', render: (r) => (
                  <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{r.createdAt || r.timestamp ? new Date(r.createdAt || r.timestamp).toLocaleString() : '—'}</span>
                )},
                { key: 'event', header: 'Event', render: (r) => (
                  <Pill tone={EVENT_TONE[r.eventType || r.action] || 'muted'} size="sm">{r.eventType || r.action || '—'}</Pill>
                )},
                { key: 'resource', header: 'Subject', render: (r) => (
                  <span className="text-[12px] text-v2-ink">{r.resource || r.apiKeyId?.slice(0, 12) + '…' || '—'}</span>
                )},
                { key: 'detail', header: 'Details', render: (r) => (
                  <span className="text-[11.5px] text-v2-ink-secondary">{r.message || r.reason || r.details || '—'}</span>
                )},
              ]}
              rows={recentEvents}
              rowKey={(r) => r.id || `${r.createdAt}-${r.eventType}`}
            />
          )}
        </Card.Body>
      </Card>
    </>
  );
}

/* ── Keys tab — compact mirror of /gateway-keys inventory ─────── */
function KeysTab({ navigate, keys, keyStats }) {
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <MetricCard label="Total keys"  value={keyStats.total} tone="cyan" />
        <MetricCard label="Active"      value={keyStats.active} tone="success" />
        <MetricCard label="Gateway keys" value={keyStats.gatewayKeys} tone="action" />
        <MetricCard label="SDK keys"    value={keyStats.sdkKeys} tone="trust" />
      </div>

      <Card>
        <Card.Header title={`API keys · ${keys.length}`} actions={
          <Button variant="primary" size="sm" iconLeft={<Key size={13} />}
            onClick={() => navigate('/gateway-keys')}>Open full inventory</Button>
        } />
        <Card.Body>
          {keys.length === 0 ? (
            <EmptyState icon={<Key size={20} />} title="No API keys provisioned"
              description="Create a Gateway or SDK key to enable protected agent invocation."
              primaryAction={<Button variant="primary" onClick={() => navigate('/gateway-keys')}>Open API Keys</Button>} />
          ) : (
            <Table
              ariaLabel="API keys"
              density="compact"
              columns={[
                { key: 'name', header: 'Key', render: (r) => (
                  <div>
                    <div className="font-semibold text-v2-ink text-[12.5px]">{r.name || r.label}</div>
                    <div className="text-[10.5px] text-v2-ink-muted font-mono-v2">{(r.id || '').slice(0, 16)}…</div>
                  </div>
                )},
                { key: 'scope', header: 'Scope', render: (r) => (
                  <Pill tone="muted" size="sm">{r.scope || 'tenant-wide'}</Pill>
                )},
                { key: 'created', header: 'Created', render: (r) => (
                  <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '—'}</span>
                )},
                { key: 'expires', header: 'Expires', render: (r) => {
                  if (!r.expiresAt) return <span className="text-[11px] text-v2-ink-muted">never</span>;
                  const days = Math.round((new Date(r.expiresAt).getTime() - Date.now()) / 86400000);
                  const tone = days < 0 ? 'danger' : days <= 30 ? 'warning' : 'success';
                  return <Pill tone={tone} size="sm">{days < 0 ? `expired ${-days}d ago` : `in ${days}d`}</Pill>;
                }},
                { key: 'status', header: 'Status', render: (r) => (
                  <Pill tone={(r.status || 'ACTIVE') === 'ACTIVE' ? 'success' : (r.status === 'REVOKED' ? 'danger' : 'warning')} size="sm" dot>
                    {(r.status || 'ACTIVE').toLowerCase()}
                  </Pill>
                )},
              ]}
              rows={keys.slice(0, 20)}
              rowKey={(r) => r.id}
              onRowClick={() => navigate('/gateway-keys')}
            />
          )}
        </Card.Body>
      </Card>
    </>
  );
}

/* ── Security tab ──────────────────────────────────────────────── */
function SecurityTab({ securityEvents, auditStats, loading }) {
  const [facets, setFacets] = useState(() => ({ eventType: new Set() }));
  const toggleFacet = (gKey, oKey, c) => setFacets(p => {
    const n = new Set(p[gKey] || []);
    c ? n.add(oKey) : n.delete(oKey);
    return { ...p, [gKey]: n };
  });
  const clearFacets = () => setFacets({ eventType: new Set() });

  const FACET_GROUPS = useMemo(() => ([
    { key: 'eventType', label: 'Event type', options: Array.from(SECURITY_EVENT_TYPES).map(t => ({ key: t, label: t.replace(/_/g, ' ').toLowerCase() })) },
  ]), []);

  const filtered = useMemo(() => securityEvents.filter(e => {
    if (facets.eventType.size > 0 && !facets.eventType.has(e.eventType || e.action)) return false;
    return true;
  }), [securityEvents, facets]);

  const facetCounts = useMemo(() => {
    const counts = { eventType: {} };
    for (const opt of FACET_GROUPS[0].options) {
      counts.eventType[opt.key] = securityEvents.filter(e => (e.eventType || e.action) === opt.key).length;
    }
    return counts;
  }, [securityEvents, FACET_GROUPS]);

  const facetGroupsWithCounts = useMemo(() => FACET_GROUPS.map(g => ({
    ...g,
    options: g.options.map(o => ({
      ...o, count: facetCounts[g.key]?.[o.key] || 0, checked: facets[g.key]?.has(o.key) || false,
    })),
  })), [facetCounts, facets, FACET_GROUPS]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    for (const g of FACET_GROUPS) {
      for (const optKey of facets[g.key] || []) {
        const opt = g.options.find(o => o.key === optKey);
        chips.push({ groupKey: g.key, optionKey: optKey, label: `${g.label}: ${opt?.label || optKey}` });
      }
    }
    return chips;
  }, [facets, FACET_GROUPS]);

  const pg = usePaginationState(50);
  const visibleRows = pg.paginate(filtered);
  useEffect(() => { pg.setPage(0); }, [filtered.length]); // eslint-disable-line

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <MetricCard label="Auth failures" value={auditStats['AUTH_FAILURE'] || 0}
          tone={(auditStats['AUTH_FAILURE'] || 0) > 0 ? 'danger' : 'success'} />
        <MetricCard label="IP blocks" value={auditStats['IP_BLOCKED'] || 0}
          tone={(auditStats['IP_BLOCKED'] || 0) > 0 ? 'danger' : 'success'} />
        <MetricCard label="Rate limit hits" value={auditStats['RATE_LIMIT_HIT'] || 0}
          tone={(auditStats['RATE_LIMIT_HIT'] || 0) > 0 ? 'warning' : 'success'} />
        <MetricCard label="Keys revoked" value={auditStats['REVOKED'] || 0} tone="muted" />
      </div>

      {activeFilterChips.length > 0 && (
        <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-4 py-2.5 mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Filters</span>
          {activeFilterChips.map((c) => (
            <button key={`${c.groupKey}:${c.optionKey}`} onClick={() => toggleFacet(c.groupKey, c.optionKey, false)}
              className="inline-flex items-center gap-1 text-[11.5px] font-medium bg-v2-action-soft text-v2-action-strong rounded-v2-pill px-2.5 py-0.5 hover:bg-v2-action hover:text-white">
              {c.label} ×
            </button>
          ))}
          <button onClick={clearFacets} className="ml-auto text-[11.5px] text-v2-ink-muted hover:text-v2-ink">Clear all</button>
        </div>
      )}

      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <FacetRail groups={facetGroupsWithCounts} onToggle={toggleFacet} ariaLabel="Security event filters" />

        {filtered.length === 0 ? (
          <EmptyState icon={<Shield size={20} />}
            title={securityEvents.length === 0 ? 'No security events' : 'No events match filters'}
            description={securityEvents.length === 0
              ? loading ? 'Loading…' : 'All quiet on the security front — auth failures and rate-limit blocks would appear here.'
              : 'Adjust filters or clear them to see more.'}
            primaryAction={securityEvents.length > 0
              ? <Button variant="primary" onClick={clearFacets}>Clear filters</Button>
              : undefined} />
        ) : (
          <Table
            ariaLabel="Security event log"
            density="compact"
            columns={[
              { key: 'event', header: 'Event', render: (r) => (
                <Pill tone={EVENT_TONE[r.eventType || r.action] || 'muted'} size="sm" dot>{r.eventType || r.action}</Pill>
              )},
              { key: 'key', header: 'Key', render: (r) => (
                <code className="font-mono-v2 text-[11px] text-v2-ink-secondary bg-v2-muted px-1.5 py-0.5 rounded-v2-sm">
                  {r.apiKeyId ? r.apiKeyId.slice(0, 8) + '…' : '—'}
                </code>
              )},
              { key: 'ip', header: 'Source IP', render: (r) => (
                <span className="font-mono-v2 text-[11.5px] text-v2-ink-secondary">{r.sourceIp || '—'}</span>
              )},
              { key: 'detail', header: 'Details', render: (r) => (
                <span className="text-[11.5px] text-v2-ink-secondary">{(r.reason || r.details || '').slice(0, 80)}</span>
              )},
              { key: 'time', header: 'When', align: 'right', render: (r) => (
                <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{fmtRelative(r.createdAt || r.timestamp)}</span>
              )},
            ]}
            rows={visibleRows}
            rowKey={(r) => r.id}
          />
        )}
      </div>

      <div className="grid gap-5 mt-3 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <div />
        <Pagination totalItems={filtered.length} page={pg.page} pageSize={pg.pageSize}
          onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize} noun="events" />
      </div>
    </>
  );
}

/* ── Audit tab — full audit trail with pagination ──────────────── */
function AuditTab({ audit, loading }) {
  const [facets, setFacets] = useState(() => ({ eventType: new Set() }));
  const toggleFacet = (gKey, oKey, c) => setFacets(p => {
    const n = new Set(p[gKey] || []);
    c ? n.add(oKey) : n.delete(oKey);
    return { ...p, [gKey]: n };
  });
  const clearFacets = () => setFacets({ eventType: new Set() });

  const eventTypes = useMemo(() => {
    const s = new Set();
    for (const e of audit) s.add(e.eventType || e.action || 'OTHER');
    return Array.from(s).sort();
  }, [audit]);

  const FACET_GROUPS = useMemo(() => ([
    { key: 'eventType', label: 'Event type', options: eventTypes.map(t => ({ key: t, label: t.replace(/_/g, ' ').toLowerCase() })) },
  ]), [eventTypes]);

  const filtered = useMemo(() => audit.filter(e => {
    if (facets.eventType.size > 0 && !facets.eventType.has(e.eventType || e.action)) return false;
    return true;
  }), [audit, facets]);

  const facetCounts = useMemo(() => {
    const counts = { eventType: {} };
    for (const t of eventTypes) {
      counts.eventType[t] = audit.filter(e => (e.eventType || e.action) === t).length;
    }
    return counts;
  }, [audit, eventTypes]);

  const facetGroupsWithCounts = useMemo(() => FACET_GROUPS.map(g => ({
    ...g,
    options: g.options.map(o => ({
      ...o, count: facetCounts[g.key]?.[o.key] || 0, checked: facets[g.key]?.has(o.key) || false,
    })),
  })), [facetCounts, facets, FACET_GROUPS]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    for (const g of FACET_GROUPS) {
      for (const optKey of facets[g.key] || []) {
        const opt = g.options.find(o => o.key === optKey);
        chips.push({ groupKey: g.key, optionKey: optKey, label: `${g.label}: ${opt?.label || optKey}` });
      }
    }
    return chips;
  }, [facets, FACET_GROUPS]);

  const pg = usePaginationState(50);
  const visibleRows = pg.paginate(filtered);
  useEffect(() => { pg.setPage(0); }, [filtered.length]); // eslint-disable-line

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <MetricCard label="Total events" value={audit.length} tone="cyan" />
        <MetricCard label="Created" value={audit.filter(e => (e.eventType || e.action) === 'CREATED').length} tone="success" />
        <MetricCard label="Revoked" value={audit.filter(e => (e.eventType || e.action) === 'REVOKED').length} tone="muted" />
        <MetricCard label="Event types" value={eventTypes.length} tone="trust" />
      </div>

      {activeFilterChips.length > 0 && (
        <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-4 py-2.5 mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Filters</span>
          {activeFilterChips.map((c) => (
            <button key={`${c.groupKey}:${c.optionKey}`} onClick={() => toggleFacet(c.groupKey, c.optionKey, false)}
              className="inline-flex items-center gap-1 text-[11.5px] font-medium bg-v2-action-soft text-v2-action-strong rounded-v2-pill px-2.5 py-0.5 hover:bg-v2-action hover:text-white">
              {c.label} ×
            </button>
          ))}
          <button onClick={clearFacets} className="ml-auto text-[11.5px] text-v2-ink-muted hover:text-v2-ink">Clear all</button>
        </div>
      )}

      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <FacetRail groups={facetGroupsWithCounts} onToggle={toggleFacet} ariaLabel="Audit filters" />

        {filtered.length === 0 ? (
          <EmptyState icon={<FileText size={20} />}
            title={audit.length === 0 ? 'No audit events' : 'No events match filters'}
            description={audit.length === 0
              ? loading ? 'Loading…' : 'Key lifecycle events (created, rotated, suspended, revoked) will appear here.'
              : 'Adjust filters or clear them to see more.'} />
        ) : (
          <Table
            ariaLabel="Full audit trail"
            density="compact"
            columns={[
              { key: 'time', header: 'When', render: (r) => (
                <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{r.createdAt || r.timestamp ? new Date(r.createdAt || r.timestamp).toLocaleString() : '—'}</span>
              )},
              { key: 'event', header: 'Event', render: (r) => (
                <Pill tone={EVENT_TONE[r.eventType || r.action] || 'muted'} size="sm">{r.eventType || r.action || '—'}</Pill>
              )},
              { key: 'key', header: 'Key', render: (r) => (
                <code className="font-mono-v2 text-[11px] text-v2-ink-secondary bg-v2-muted px-1.5 py-0.5 rounded-v2-sm">
                  {r.apiKeyId ? r.apiKeyId.slice(0, 8) + '…' : (r.resource || '—')}
                </code>
              )},
              { key: 'user', header: 'Actor', render: (r) => (
                <span className="text-[11.5px] text-v2-ink-secondary">{r.actor || r.userId || r.userEmail || '—'}</span>
              )},
              { key: 'detail', header: 'Details', render: (r) => (
                <span className="text-[11.5px] text-v2-ink-secondary">{(r.reason || r.details || r.message || '').slice(0, 80)}</span>
              )},
            ]}
            rows={visibleRows}
            rowKey={(r) => r.id || `${r.createdAt}-${r.eventType}`}
          />
        )}
      </div>

      <div className="grid gap-5 mt-3 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <div />
        <Pagination totalItems={filtered.length} page={pg.page} pageSize={pg.pageSize}
          onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize} noun="events" />
      </div>
    </>
  );
}

export default function ResolvedGatewayDashboardPage(props) {
  const url = typeof window !== 'undefined' ? new URL(window.location.href) : null;
  const legacy = url?.searchParams?.get('legacy') === '1';
  if (legacy || !isRebirthAdministerEnabled()) return <GatewayDashboardPage {...props} />;
  return <GatewayDashboardPageV2Inner {...props} />;
}

export { GatewayDashboardPageV2Inner };
